// Picking Performance Analysis tab.
//
// Hits ONE backend endpoint (`/api/picking/analyze`) so the whole view
// loads in a single request — workaround for the Citrix proxy that
// kept dropping our 3rd parallel fetch and surfacing "Couldn't reach
// the server" toasts. One endpoint also means one retry chance covers
// everything; if it still fails after the safeFetch retry, we offer a
// big "Try again" button right where the data should be instead of a
// dead-end toast.
//
// The view shows, side-by-side, **two pools** (GRF order-pickers and
// GRT reach-truck), each with:
//   • KPI strip — Total picks, Productivity (picks/hr), Efficiency vs
//     benchmark, Avg pick time, Peak-hour productivity, Pareto top-20
//     concentration.
//   • Two heat-grids over hour-of-day × weekday — RED for the busiest
//     hours (volume) and BLUE for the lowest-productivity hours
//     (process bottlenecks). Different palettes so the eye instantly
//     separates "high-volume good" from "high-volume slow".
//
// Default benchmarks are 6 picks/hr (GRF) and 15 picks/hr (GRT) — the
// floor manager can override either at any time.

import { Component, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  TrendingUp, Activity, Users, Gauge, Clock, Flame,
  RotateCcw, AlertCircle, Calendar, FileSpreadsheet, Filter,
} from 'lucide-react';
import { safeFetchJson } from '@/utils/safeFetch';
import { exportPickingAnalyze } from '@/services/api';
import RotaPlanCard from './RotaPlanCard';
import ResourceMapPanel from './ResourceMapPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// React error boundary that wraps the data sections so a malformed
// payload (rare, but possible after a partial XHR retry) shows an
// inline "Try again" instead of blanking out the whole tab.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // Log to the console — the user-facing message stays friendly.
    // eslint-disable-next-line no-console
    console.error('Analyze render error:', err, info);
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <Card className="border-amber-300 bg-amber-50" data-testid="perf-render-error">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">We couldn&rsquo;t draw the analysis</p>
            <p className="text-xs text-amber-800 mt-1">
              {String(this.state.err?.message || this.state.err || 'Unexpected error')}
            </p>
            <p className="text-[11px] text-amber-700/80 mt-2">
              Press the button below to re-run with the same selection.
            </p>
          </div>
          <Button size="sm" onClick={() => { this.setState({ err: null }); this.props.onReset?.(); }}
                  className="bg-amber-600 hover:bg-amber-700 text-white">
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
}
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const fmtPph = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}/hr`);
const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(0)}%`);
const fmtMinHr = (m) => {
  if (m == null) return '—';
  const v = Math.round(m);
  return v < 60 ? `${v}m` : `${Math.floor(v / 60)}h ${v % 60}m`;
};

// ---- Combined heatmap colour ----
// One palette ranging from light-blue (productivity below benchmark)
// through neutral light-grey (at benchmark) to light-red (above
// benchmark). Lightweight on the eyes, prints well, and the same cell
// is comparable across windows because the scale is anchored to the
// benchmark — not to the observed max.
//   • productivity 0   →   #cfe3ff (light blue)
//   • productivity = benchmark → #f3f4f6 (neutral)
//   • productivity ≥ 1.5× benchmark → #ffd0d0 (light red)
const cellColor = (productivity, benchmark) => {
  if (productivity == null) return '#f8fafc';
  const bench = Math.max(benchmark || 0, 0.0001);
  const ratio = productivity / bench; // 0=very slow, 1=on target, >1=above
  if (ratio < 1) {
    // 0 .. 1 → light-blue (#cfe3ff) → neutral (#f3f4f6)
    const t = Math.max(Math.min(ratio, 1), 0); // 0..1
    const r = Math.round(207 + (243 - 207) * t);
    const g = Math.round(227 + (244 - 227) * t);
    const b = Math.round(255 + (246 - 255) * t);
    return `rgb(${r},${g},${b})`;
  }
  // 1 .. 1.5+ → neutral → light-red (#ffd0d0)
  const t = Math.max(Math.min((ratio - 1) / 0.5, 1), 0);
  const r = Math.round(243 + (255 - 243) * t);
  const g = Math.round(244 + (208 - 244) * t);
  const b = Math.round(246 + (208 - 246) * t);
  return `rgb(${r},${g},${b})`;
};

const Kpi = ({ icon: Icon, label, value, sub, accent = '#0f172a', testid }) => (
  <Card className="flex-1 min-w-[150px]" data-testid={testid}>
    <CardContent className="p-3 space-y-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

const HeatGrid = ({ data, benchmark, label }) => {
  // Hardened against missing data — every accessor uses optional
  // chaining + sensible fallbacks so a malformed payload renders an
  // empty grid instead of crashing the whole tab.
  const hours = data?.hours || [];
  const weekdays = data?.weekday_labels || [];
  const busy = data?.busy || [];
  const productivity = data?.productivity || [];
  const cellW = 56, cellH = 26, gutterL = 32, gutterT = 20;
  if (hours.length === 0 || weekdays.length === 0) {
    return <div className="text-xs text-muted-foreground p-3">No heatmap data.</div>;
  }
  const w = gutterL + cellW * weekdays.length + 4;
  const h = gutterT + cellH * hours.length + 4;
  const cellLabel = (hi, di) => {
    const tasks = busy?.[hi]?.[di] || 0;
    if (!tasks) return '';
    return tasks > 999 ? `${(tasks / 1000).toFixed(1)}k` : `${tasks}`;
  };
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <svg width={w} height={h} aria-label={label}>
        {weekdays.map((d, i) => (
          <text key={d} x={gutterL + i * cellW + cellW / 2} y={14}
                textAnchor="middle" className="fill-slate-500" style={{ fontSize: 11, fontWeight: 500 }}>
            {d}
          </text>
        ))}
        {hours.map((hr, i) => (
          <text key={hr} x={gutterL - 6} y={gutterT + i * cellH + cellH / 2 + 3}
                textAnchor="end" className="fill-slate-500" style={{ fontSize: 11 }}>
            {hr}
          </text>
        ))}
        {hours.map((hr, hi) => weekdays.map((d, di) => {
          const tasks = busy?.[hi]?.[di] || 0;
          const pph = productivity?.[hi]?.[di];
          const fill = cellColor(pph, benchmark);
          const cx = gutterL + di * cellW + cellW / 2;
          const cy = gutterT + hi * cellH + cellH / 2 + 4;
          // Foreground stays slate-700 always — palette is light enough
          // for black text to read everywhere, no contrast flips needed.
          return (
            <g key={`${hi}-${di}`}>
              <rect x={gutterL + di * cellW + 1} y={gutterT + hi * cellH + 1}
                    width={cellW - 2} height={cellH - 2}
                    fill={fill} stroke="#e2e8f0" strokeWidth="0.5" />
              <text x={cx} y={cy} textAnchor="middle"
                    style={{ fontSize: 11, fontWeight: 500, pointerEvents: 'none' }}
                    fill="#334155">
                {cellLabel(hi, di)}
              </text>
              <title>{
                `${d} ${hr}:00 — ${tasks || 0} task${tasks === 1 ? '' : 's'}` +
                (pph != null ? ` · ${pph.toFixed(1)} picks/hr` : '')
              }</title>
            </g>
          );
        }))}
      </svg>
    </div>
  );
};

// Per-Wachtrij rollup table for a single pool. Carries its own
// "Below benchmark only" filter so the floor manager can collapse the
// rollup to just the queues that need attention. The filter state is
// persisted per pool in localStorage so it survives the page refresh.
const PerWachtrijTable = ({ pool, rows, benchmark }) => {
  const storageKey = `pk-wq-below-only-${pool}`;
  const [belowOnly, setBelowOnly] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, belowOnly ? '1' : '0'); } catch { /* quota */ }
  }, [storageKey, belowOnly]);

  // "Below benchmark" === efficiency_pct < 100. We deliberately use the
  // strict-less comparison so a queue sitting exactly at the benchmark
  // (100 %) is treated as "on target" and falls out of the filtered
  // view — the planner is hunting for queues that drag the pool down.
  const visible = useMemo(() => {
    if (!belowOnly) return rows;
    return rows.filter(w => (w.efficiency_pct ?? 0) < 100);
  }, [rows, belowOnly]);

  const belowCount = useMemo(
    () => rows.filter(w => (w.efficiency_pct ?? 0) < 100).length,
    [rows]
  );

  return (
    <div className="mt-6" data-testid={`pool-${pool}-per-wachtrij`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h4 className="text-sm font-semibold text-slate-700">
          Per Wachtrij — efficiency vs <span className="tabular-nums">{benchmark}</span> picks/hr
        </h4>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant={belowOnly ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2.5 text-[11px]"
            onClick={() => setBelowOnly(v => !v)}
            data-testid={`wq-below-only-${pool}`}
            title={belowOnly
              ? 'Showing only queues below 100% efficiency. Click to show all.'
              : `Show only the ${belowCount} queue(s) below the benchmark.`}
          >
            <Filter className="h-3 w-3 mr-1" />
            Below benchmark only
            {belowOnly && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                {belowCount}
              </Badge>
            )}
          </Button>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {belowOnly
              ? `${visible.length} of ${rows.length} queue${rows.length === 1 ? '' : 's'}`
              : `${rows.length} queue${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Wachtrij</th>
              <th className="text-right px-3 py-2 font-medium">Picks</th>
              <th className="text-right px-3 py-2 font-medium">Days</th>
              <th className="text-right px-3 py-2 font-medium">Hours</th>
              <th className="text-right px-3 py-2 font-medium">Picks / hr</th>
              <th className="text-right px-3 py-2 font-medium">Avg pick (min)</th>
              <th className="text-right px-3 py-2 font-medium">Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-foreground italic">
                  {belowOnly
                    ? 'All queues are at or above the benchmark. Nice. 🎯'
                    : 'No queues in this period.'}
                </td>
              </tr>
            ) : visible.map(w => {
              const eff = w.efficiency_pct ?? 0;
              const effColor = eff >= 100 ? '#10b981' : eff >= 70 ? '#f59e0b' : '#ef4444';
              return (
                <tr key={w.wachtrij} className="border-t" data-testid={`wq-row-${pool}-${w.wachtrij}`}>
                  <td className="px-3 py-2 font-mono">{w.wachtrij}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(w.picks)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.days_active}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.active_hours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.picks_per_hour.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.avg_pick_time_min.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: effColor }}>
                    {eff.toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const PoolBlock = ({ pool, data, benchmark, onBenchmarkChange }) => {
  // Defensive defaults — every accessor below assumes the shape but
  // gracefully degrades when something is missing (rare partial-XHR
  // payload, never seen in practice, but we don't want one bad cell
  // to nuke the page).
  const k = data?.kpis || {};
  const hasData = (k.total_picks || 0) > 0;
  // Heatmap resource selector: "all" pools every resource together
  // (default), or pick a specific GR? code. We persist the choice per
  // pool so flipping between GRF and GRT remembers the last selection.
  const storageKey = `pk-heat-res-${pool}`;
  const [heatResource, setHeatResource] = useState(() => {
    try { return localStorage.getItem(storageKey) || 'all'; } catch { return 'all'; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, heatResource); } catch { /* quota */ }
  }, [storageKey, heatResource]);

  const resourceList = useMemo(() => {
    return Object.keys(data?.per_resource_heatmap || {}).sort();
  }, [data?.per_resource_heatmap]);
  // Reset selector if the previously-chosen resource is no longer in
  // the data (e.g. user switched the period to a window where this
  // worker didn't operate).
  useEffect(() => {
    if (heatResource !== 'all' && !resourceList.includes(heatResource)) {
      setHeatResource('all');
    }
  }, [resourceList, heatResource]);

  const heat = useMemo(() => {
    if (heatResource === 'all') return data?.heatmap;
    return data?.per_resource_heatmap?.[heatResource] || data?.heatmap;
  }, [heatResource, data]);
  const heatBench = benchmark;

  // Productivity colour cue for the KPI card: green if above benchmark,
  // amber if 70-100 %, red below 70 %.
  const effColor = k.efficiency_pct >= 100 ? '#10b981'
    : k.efficiency_pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <Card data-testid={`pool-${pool}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            {data.label}
            {hasData && (
              <Badge variant="outline" className="ml-1 text-[10px] font-normal">
                {k.resource_count} active
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground" htmlFor={`bench-${pool}`}>Benchmark</Label>
            <Input id={`bench-${pool}`} type="number" min={1} max={100} step={0.5}
                   value={benchmark}
                   onChange={(e) => onBenchmarkChange(Number(e.target.value) || 0)}
                   className="w-20 h-8 text-sm" data-testid={`bench-${pool}-input`} />
            <span className="text-xs text-muted-foreground">picks/hr</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasData && (
          <div className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
            No confirmed picks for this pool in the selected window.
          </div>
        )}
        {hasData && (
          <>
            <div className="flex flex-wrap gap-2">
              <Kpi icon={Activity} label="Total workload" value={fmtNum(k.total_picks)}
                   sub={`${fmtNum(k.active_hours)} active hrs`}
                   testid={`${pool}-kpi-volume`} />
              <Kpi icon={TrendingUp} label="Productivity" value={fmtPph(k.picks_per_hour)}
                   sub={`peak ${fmtPph(k.peak_hour_pph)} ${k.peak_hour_label || ''}`}
                   accent="#2D8C8C"
                   testid={`${pool}-kpi-productivity`} />
              <Kpi icon={Gauge} label="Efficiency vs target" value={fmtPct(k.efficiency_pct)}
                   sub={`vs ${benchmark} picks/hr target`}
                   accent={effColor}
                   testid={`${pool}-kpi-efficiency`} />
              <Kpi icon={Clock} label="Avg pick time" value={fmtNum(k.avg_pick_time_min)}
                   sub={`min · idle ${fmtMinHr(k.idle_min)}`}
                   testid={`${pool}-kpi-pick-time`} />
              <Kpi icon={Users} label={`Top ${k.pareto_top_n} share`}
                   value={fmtPct(k.pareto_top_share_pct)}
                   sub="of total picks (Pareto 80/20)"
                   testid={`${pool}-kpi-pareto`} />
            </div>

            {/* Heatmap resource selector — switches both grids at once. */}
            <div className="flex items-center gap-2 flex-wrap pt-2">
              <Label className="text-xs text-muted-foreground" htmlFor={`heatres-${pool}`}>
                Heatmap for
              </Label>
              <select
                id={`heatres-${pool}`}
                data-testid={`heatres-${pool}`}
                value={heatResource}
                onChange={(e) => setHeatResource(e.target.value)}
                className="h-8 text-sm border rounded-md px-2 bg-white"
              >
                <option value="all">All {pool.toUpperCase()} resources pooled</option>
                {resourceList.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {heatResource !== 'all' && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  {heatResource}
                </Badge>
              )}
            </div>

            <div className="pt-2">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Flame className="h-4 w-4 text-rose-500" />
                Productivity heatmap
                <span className="text-xs text-muted-foreground font-normal">
                  (number = tasks performed · light blue = below benchmark · light red = above)
                </span>
              </div>
              <HeatGrid data={heat} benchmark={heatBench}
                        label="Tasks per hour with productivity colouring" />
              {/* Tiny inline legend so a first-time user understands the
                  scale without hovering every cell. */}
              <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                <span>Below target</span>
                <div className="h-3 flex rounded overflow-hidden border border-slate-200" style={{ minWidth: 160 }}>
                  <div style={{ flex: 1, background: 'rgb(207,227,255)' }} />
                  <div style={{ flex: 1, background: 'rgb(225,235,250)' }} />
                  <div style={{ flex: 1, background: 'rgb(243,244,246)' }} />
                  <div style={{ flex: 1, background: 'rgb(252,224,224)' }} />
                  <div style={{ flex: 1, background: 'rgb(255,208,208)' }} />
                </div>
                <span>Above target</span>
                <span className="ml-2 font-medium text-slate-600">
                  Target: {heatBench} picks/hr
                </span>
              </div>
            </div>

            {/* Per-Wachtrij rollup — every queue's picks/hour, recomputed
                against the (manually-set) benchmark for this pool.
                Adjusting the benchmark + Re-run refreshes every row.
                "Below benchmark only" filter is per-pool, persisted in
                localStorage so the floor manager keeps the same view
                between visits. */}
            {Array.isArray(data.per_wachtrij) && data.per_wachtrij.length > 0 && (
              <PerWachtrijTable
                pool={pool}
                rows={data.per_wachtrij}
                benchmark={benchmark}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Per-person efficiency block — pulls from the presence-cross result so
// the user picks a name and sees that person's whole period: which
// resources they used (primary + any in-day switches captured in the
// presence "notes" column as `HH:MM - GRXxxx; …`), how many picks per
// resource, the time/date frames each resource was used, and the total
// hours worked. All numbers are real (no mocks) — the backend builds
// the timeline from the presence document and matches it against the
// picking_tasks pool.
const PerPersonBlock = ({ people, benchGrf, benchGrt }) => {
  // Persist last-selected person across sessions so the floor manager
  // doesn't rebuild context on every page reload.
  const [selectedId, setSelectedId] = useState(() => {
    try { return localStorage.getItem('pk-pp-emp') || ''; } catch { return ''; }
  });
  // Default to the busiest person (top of `people`, already sorted) on
  // first load or when the previous selection is no longer in the data
  // (window changed).
  useEffect(() => {
    if (!people || people.length === 0) return;
    if (selectedId && people.some(p => p.employee_id === selectedId)) return;
    setSelectedId(people[0].employee_id);
  }, [people, selectedId]);
  useEffect(() => {
    try { localStorage.setItem('pk-pp-emp', selectedId); } catch { /* quota */ }
  }, [selectedId]);

  const person = people?.find(p => p.employee_id === selectedId);

  if (!people || people.length === 0) {
    return (
      <Card data-testid="per-person-block">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="h-4 w-4" /> Efficiency by person
          </CardTitle>
          <CardDescription className="text-xs">
            Cross-references the presence sheet (Workforce Planning) with the picking pool. Pick a name to see resources used, time frames, picks delivered and total hours.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
            No presence data with GR-resources for this period.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="per-person-block">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="h-4 w-4" /> Efficiency by person
            </CardTitle>
            <CardDescription className="text-xs">
              Resources used, time frames, picks per resource, total hours — built from presence + picking_tasks for the selected period.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground" htmlFor="pp-select">Person</Label>
            <select
              id="pp-select"
              data-testid="per-person-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="h-8 text-sm border rounded-md px-2 bg-white max-w-[260px]"
            >
              {people.map(p => (
                <option key={p.employee_id} value={p.employee_id}>
                  {p.name} · {p.total_picks} picks
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!person && (
          <div className="text-sm text-muted-foreground">Select a person above.</div>
        )}
        {person && (
          <>
            <div className="flex flex-wrap gap-2">
              <Kpi icon={Users} label="Person" value={person.name}
                   sub={`primary ${person.primary_pool}`}
                   testid="pp-kpi-name" />
              <Kpi icon={Activity} label="Total picks" value={fmtNum(person.total_picks)}
                   sub={`across ${person.resources.length} resource(s)`}
                   testid="pp-kpi-picks" />
              <Kpi icon={Clock} label="Total hours" value={`${person.total_hours}h`}
                   sub="active time on these resources"
                   testid="pp-kpi-hours" />
            </div>

            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Resource</th>
                    <th className="text-right px-3 py-2 font-medium">Picks</th>
                    <th className="text-right px-3 py-2 font-medium">Hours</th>
                    <th className="text-right px-3 py-2 font-medium">Picks / hr</th>
                    <th className="text-right px-3 py-2 font-medium">Efficiency</th>
                    <th className="text-left px-3 py-2 font-medium">Time frames</th>
                  </tr>
                </thead>
                <tbody>
                  {person.resources.map(r => {
                    const bench = r.resource.startsWith('GRF') ? benchGrf : benchGrt;
                    const eff = r.efficiency_pct;
                    const effColor = eff >= 100 ? '#10b981'
                      : eff >= 70 ? '#f59e0b' : '#ef4444';
                    return (
                      <tr key={r.resource} className="border-t" data-testid={`pp-row-${r.resource}`}>
                        <td className="px-3 py-2 font-mono">{r.resource}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.picks)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.picks_per_hour.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: effColor }}>
                          {eff.toFixed(0)}% <span className="text-[10px] text-muted-foreground font-normal">vs {bench}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {r.intervals.slice(0, 6).map(it => (
                            <span key={`${it.date}${it.start}`} className="inline-block mr-2">
                              {it.date} {it.start}–{it.end}
                            </span>
                          ))}
                          {r.intervals.length > 6 && (
                            <span className="text-[11px] italic">+{r.intervals.length - 6} more</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const PerformanceAnalysisTab = ({ bevFrom, bevTo, bewerkerPrefix, selectedWorkers, selectedWachtrijen }) => {
  // Period and filter chain come from the top filter bar; this tab
  // never owns its own date or pool selector. Mirrors the Dashboard
  // tab exactly so the two views can never disagree.
  const periodA = useMemo(() => ({
    from: (bevFrom || '').split('T')[0],
    to: (bevTo || '').split('T')[0],
  }), [bevFrom, bevTo]);
  const periodLength = useMemo(() => {
    if (!periodA.from || !periodA.to) return 0;
    return Math.round((new Date(periodA.to) - new Date(periodA.from)) / 86400000) + 1;
  }, [periodA.from, periodA.to]);

  // Normalise the top filter chain into the form the backend expects.
  const poolParam = useMemo(() => {
    const p = (bewerkerPrefix || 'ALL').toUpperCase();
    if (p === 'GRF') return 'grf';
    if (p === 'GRT') return 'grt';
    return 'all';
  }, [bewerkerPrefix]);
  const resourcesParam = useMemo(
    () => Array.isArray(selectedWorkers) && selectedWorkers.length
      ? selectedWorkers.join(',')
      : '',
    [selectedWorkers],
  );
  const wachtrijParam = useMemo(
    () => Array.isArray(selectedWachtrijen) && selectedWachtrijen.length
      ? selectedWachtrijen.join(',')
      : '',
    [selectedWachtrijen],
  );
  // Human-readable summary chips in the controls card so the user
  // never wonders which selection drove the numbers.
  const filterChips = useMemo(() => {
    const out = [];
    if (poolParam !== 'all') out.push(`Pool ${poolParam.toUpperCase()}`);
    if (resourcesParam) {
      const list = resourcesParam.split(',');
      out.push(list.length === 1
        ? `Bewerker ${list[0]}`
        : `${list.length} bewerkers`);
    }
    if (wachtrijParam) {
      const list = wachtrijParam.split(',');
      out.push(list.length === 1
        ? `Wachtrij ${list[0]}`
        : `${list.length} wachtrijen`);
    }
    return out;
  }, [poolParam, resourcesParam, wachtrijParam]);

  // Per-pool benchmarks. Defaults match the warehouse's published
  // targets (GRF order-pickers ≈ 6/hr, GRT reach-truck ≈ 15/hr).
  // Persisted in localStorage so the user doesn't re-type after a
  // browser refresh.
  const [benchGrf, setBenchGrf] = useState(() => Number(localStorage.getItem('pk-bench-grf')) || 6);
  const [benchGrt, setBenchGrt] = useState(() => Number(localStorage.getItem('pk-bench-grt')) || 15);
  useEffect(() => { localStorage.setItem('pk-bench-grf', String(benchGrf)); }, [benchGrf]);
  useEffect(() => { localStorage.setItem('pk-bench-grt', String(benchGrt)); }, [benchGrt]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);
  // Capture the benchmark values that drove the last successful run.
  // If the user edits one of the inputs without re-running, we surface
  // a "Re-run to apply" hint next to the button so they know the
  // numbers below are still based on the previous benchmark.
  const [lastRunBenchmarks, setLastRunBenchmarks] = useState({ grf: 0, grt: 0 });
  const benchmarksDirty = !!data && (lastRunBenchmarks.grf !== benchGrf || lastRunBenchmarks.grt !== benchGrt);

  // Wipe on filter change so we never paint stale matrix under fresh dates.
  useEffect(() => {
    setData(null); setErrorMsg(null); setLastRunAt(null);
  }, [periodA.from, periodA.to, poolParam, resourcesParam, wachtrijParam]);

  const run = async () => {
    if (!periodA.from || !periodA.to) {
      toast.error('Pick a date range in the filter bar above first');
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    try {
      const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const qs = new URLSearchParams({
        date_from: periodA.from,
        date_to: periodA.to,
        benchmark_grf: String(benchGrf),
        benchmark_grt: String(benchGrt),
        pool: poolParam,
      });
      if (resourcesParam) qs.set('resources', resourcesParam);
      if (wachtrijParam) qs.set('wachtrij', wachtrijParam);
      const res = await safeFetchJson(`${API}/api/picking/analyze?${qs.toString()}`, { token: tok });
      setData(res);
      setLastRunAt(new Date());
      setLastRunBenchmarks({ grf: benchGrf, grt: benchGrt });
    } catch (e) {
      setErrorMsg(e.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Benchmarks DON'T auto-rerun the analysis — the user edits them
  // freely and presses Re-run when ready. Avoids hammering the backend
  // on every keystroke and matches the floor-manager workflow ("change
  // target, then test it"). Period / pool / resources / wachtrij still
  // wipe stale results above; the user must click Re-run before the
  // next results land.

  const reset = () => {
    setBenchGrf(6); setBenchGrt(15);
    setData(null); setErrorMsg(null); setLastRunAt(null);
    toast.success('Analyze view reset');
  };

  // Excel export — same selection as the on-screen view, no extra
  // round trip through the user. The button is disabled until an
  // Analyse has actually been run so we never download an empty file.
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (!data) {
      toast.error('Run the analysis first, then export');
      return;
    }
    setExporting(true);
    try {
      const blob = await exportPickingAnalyze({
        date_from: periodA.from,
        date_to: periodA.to,
        benchmark_grf: benchGrf,
        benchmark_grt: benchGrt,
        pool: poolParam,
        resources: resourcesParam,
        wachtrij: wachtrijParam,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const poolSuffix = poolParam !== 'all' ? `_${poolParam.toUpperCase()}` : '';
      a.download = `picking_analyze${poolSuffix}_${periodA.from}_to_${periodA.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel export downloaded');
    } catch (e) {
      toast.error(e.message || 'Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="perf-analysis-root">
      {/* Controls */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="space-y-1 flex-1 min-w-[260px]">
              <Label className="text-xs text-muted-foreground">Selection (from filter bar above)</Label>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <Badge variant="outline" className="font-mono" data-testid="perf-period-summary">
                  <Calendar className="h-3 w-3 mr-1" />
                  {periodA.from && periodA.to
                    ? `${periodA.from} → ${periodA.to} (${periodLength}d)`
                    : 'No date range selected'}
                </Badge>
                {filterChips.map((c) => (
                  <Badge key={c} variant="outline" className="bg-[#2D8C8C]/8" data-testid="perf-filter-chip">
                    {c}
                  </Badge>
                ))}
                {lastRunAt && (
                  <span className="text-[11px] text-muted-foreground">
                    last analyzed {lastRunAt.toLocaleTimeString()}
                  </span>
                )}
                {benchmarksDirty && (
                  <span className="text-[11px] font-medium text-amber-600" data-testid="perf-bench-dirty">
                    benchmark changed — press Re-run
                  </span>
                )}
              </div>
            </div>
            <Button onClick={run}
                    disabled={loading || !periodA.from || !periodA.to}
                    data-testid="perf-run"
                    className={benchmarksDirty ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
                    title={benchmarksDirty ? 'Benchmarks changed — re-run to refresh the heatmap colours' : 'Run the analysis'}>
              <Activity className="h-4 w-4 mr-1.5" />
              {loading ? 'Loading…' : (data ? 'Re-run' : 'Analyse')}
            </Button>
            <Button variant="outline" onClick={exportExcel}
                    disabled={!data || exporting}
                    data-testid="perf-export-excel"
                    title={data ? 'Download the per-pool KPIs + heatmap as Excel' : 'Run the analysis first'}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
            <Button variant="ghost" onClick={reset} data-testid="perf-reset">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Source: confirmed tasks only (Datum bevestiging + Bevestigingstijd). Sundays excluded.
          </p>
        </CardContent>
      </Card>

      {/* Error state — inline, with a one-click retry button so the
          user has a real way out instead of a toast that fades away. */}
      {errorMsg && (
        <Card className="border-amber-300 bg-amber-50" data-testid="perf-error">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Couldn&rsquo;t load the analysis</p>
              <p className="text-xs text-amber-800 mt-1">{errorMsg}</p>
              <p className="text-[11px] text-amber-700/80 mt-2">
                Tip: if you&rsquo;re inside Citrix, the first request after a fresh session sometimes drops — the retry button below usually resolves it.
              </p>
            </div>
            <Button size="sm" onClick={run} className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="perf-retry">
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!data && !errorMsg && !loading && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Set a date range in the filter bar above and press <strong>Analyse</strong> to see
            workload, productivity and busy-hour heatmaps for both pools.
          </CardContent>
        </Card>
      )}

      {/* Data state */}
      {data && (
        <ErrorBoundary onReset={run}>
          {data.pools?.grf && (
            <PoolBlock pool="grf" data={data.pools.grf} benchmark={benchGrf} onBenchmarkChange={setBenchGrf} />
          )}
          {data.pools?.grt && (
            <PoolBlock pool="grt" data={data.pools.grt} benchmark={benchGrt} onBenchmarkChange={setBenchGrt} />
          )}
          {!data.pools?.grf && !data.pools?.grt && (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                No data matching the selected pool / resources / wachtrij in this window.
              </CardContent>
            </Card>
          )}
          <PerPersonBlock people={data.per_person || []} benchGrf={benchGrf} benchGrt={benchGrt} />
        </ErrorBoundary>
      )}

      {/* Scanner mapping — applied before everything above/below */}
      <ResourceMapPanel period={periodA} onChanged={() => { if (data) run(); }} />

      {/* Demand-aligned planning rota — reuses the selection above */}
      <RotaPlanCard period={periodA} poolParam={poolParam} />
    </div>
  );
};

export default PerformanceAnalysisTab;
